import { ApsFolderContent } from "@api/apiClient"; // Ensure correct import path
import { useGetItemVersions } from "@api/hubs";
import i18n from "@i18n/i18n";
import CloseIcon from "@mui/icons-material/Close";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Radio,
  Typography,
} from "@mui/material";
import { observer } from "mobx-react";
import React, { useEffect, useState } from "react";

interface ModelVersionSelectionProps {
  openVersionDialog: boolean;
  projectId: string;
  itemId: string;
  selectedVersions: ApsFolderContent[];
  onClose: (selectedItemId: string, selectedVersionData: any) => void;
}

const ModelVersionSelection = observer((props: ModelVersionSelectionProps) => {
  const { openVersionDialog, onClose, projectId, itemId } = props;
  const [selectedItem, setSelectedItem] = useState<ApsFolderContent | null>(null);
  const [versionData, setVersionData] = useState<ApsFolderContent[]>([]);

  const itemVersions = useGetItemVersions(projectId, itemId, {
    enabled: !!itemId,
  });

  useEffect(() => {
    itemVersions.data.sort((a: any, b: any) => b.version - a.version);
    setVersionData(itemVersions.data);
    // check for any undefined case
    const selectedVersionsArray = Array.isArray(props.selectedVersions) ? props.selectedVersions : [];
    // Create a Set of selected IDs for quick lookups
    const selectedIds = new Set(selectedVersionsArray.map((selected) => selected.id));
    // Find the first version that has an ID in the selectedIds Set
    let matchedItem = itemVersions.data.find((version) => selectedIds.has(version.id)) || itemVersions.data[0];
    setSelectedItem(matchedItem || null);
  }, [itemVersions.data]);

  const handleSelect = (item: ApsFolderContent) => {
    setSelectedItem(item);
  };

  const handleClose = () => {
    setVersionData([]);
    onClose(props.itemId, props.selectedVersions);
  };

  const handleSelectVersion = () => {
    const selectedVersionsArray = Array.isArray(props.selectedVersions) ? props.selectedVersions : [];

    selectedVersionsArray.push(selectedItem as ApsFolderContent);
    onClose(props.itemId, selectedVersionsArray);
  };

  useEffect(() => {
    if (openVersionDialog) {
      itemVersions.refetch(); // Fetch new data when dialog opens
    }
  }, [openVersionDialog, itemVersions.refetch]);

  const formatModifiedAt = (modifiedAt?: string | Date): string => {
    if (modifiedAt instanceof Date) {
      return i18n.t("datetime_long", { value: new Date(modifiedAt) });
    }
    return modifiedAt ?? " ";
  };

  return (
    <Dialog fullWidth maxWidth={"sm"} open={openVersionDialog} onClose={handleClose}>
      <DialogTitle id="alert-dialog-title" sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        {i18n.t("project-form.model-version.title")}
        <IconButton onClick={handleClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <Divider />
      <DialogContent>
        {itemVersions.isPending ? (
          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100px" }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {versionData.length > 0 ? (
              <>
                <Typography variant="h6">{versionData[0]?.name}</Typography>
                <Box>
                  <List>
                    {versionData.map((item, index) => {
                      const combinedText = (
                        <Typography variant="body2">
                          <strong>V{item.version}</strong> Uploaded by <strong>{item.modifiedByName}</strong> on <strong>{formatModifiedAt(item.modifiedAt)}</strong>
                        </Typography>
                      );
                      return (
                        <React.Fragment key={item.id}>
                          <ListItem onClick={() => handleSelect(item as ApsFolderContent)}>
                            <ListItemText primary={<Typography variant="body2">{combinedText}</Typography>} />
                            <ListItemIcon>
                              <Radio checked={selectedItem?.id === item.id} onChange={() => handleSelect(item as ApsFolderContent)} value={item.id} />
                            </ListItemIcon>
                          </ListItem>
                          {index < versionData.length - 1 && <Divider />}
                        </React.Fragment>
                      );
                    })}
                  </List>
                </Box>
              </>
            ) : (
              <Typography variant="body1"> {i18n.t("project-form.model-version.message")}</Typography>
            )}
          </>
        )}
      </DialogContent>

      <Divider />
      <DialogActions>
        <Button onClick={handleSelectVersion} color="primary" variant="contained" disabled={!selectedItem}>
          {i18n.t("project-form.model-version.button-text")}
        </Button>
      </DialogActions>
    </Dialog>
  );
});

export default ModelVersionSelection;
